import cloneDeep from "lodash/cloneDeep";
import { DefaultSettings, ISettingsProps } from "./Models.ts";
import { PrintedLanguage } from "./PrintedLanguage.ts";
import { bucketFor, fitOneDigitRanges, operationsOf } from "./FactRanges.ts";
import { fitFactLevel } from "./FactLevels.ts";
import { ProcessUrl } from "./UrlProcessor/ProcessUrl.tsx";
import {
    DoubleDigitNumbers,
    FactFamilyOptions,
    FractionDenominatorOptions,
    FractionQuestionOptions,
    MissingNumberOptions,
    MoneyQuestionOptions,
    OperationOptions,
    AlgebraQuestionOptions,
    IntegerQuestionOptions,
    RationalKindOptions,
    NumberPropertyOptions,
    ConversionQuestionOptions,
    PatternQuestionOptions,
    RomanQuestionOptions,
    LinePlotQuestionOptions,
    LinePlotScaleOptions,
    LinePlotMarkOptions,
    HistogramQuestionOptions,
    BoxPlotQuestionOptions,
    CalendarQuestionOptions,
    CalendarSpanOptions,
    CalendarMonthOptions,
    CoordinateQuestionOptions,
    CoordinateGridOptions,
    NumberLineQuestionOptions,
    PercentQuestionOptions,
    RatioQuestionOptions,
    GeometryQuestionOptions,
    ShapeQuestionOptions,
    VolumeQuestionOptions,
    CircleQuestionOptions,
    ProbabilityQuestionOptions,
    PythagoreanQuestionOptions,
    AverageQuestionOptions,
    BarGraphQuestionOptions,
    OrderOfOperationsLevelOptions,
    Operations,
    PlaceValueOptions,
    ProblemFormatOptions,
    RegroupingOptions,
    RoundingComparingOptions,
    RoundingDisplayOptions,
    RoundingPlaceOptions,
    SingleDigitNumbers,
    StoryTypeOptions,
    TimeDisplayOptions,
    TimeIntervalOptions,
    TimeQuestionOptions,
    TripleDigitNumbers,
} from "./Constants.ts";

/**
 * The settings a route may pin in the manifest, beyond its single operation.
 *
 * This is what lets a grade-level or skill page be a different worksheet rather
 * than the same one under a different heading: "2nd grade" is two-digit
 * addition and subtraction, "times tables" is the full multiplication grid,
 * "addition with regrouping" rules out the problems that do not carry.
 */
export interface RouteSettingsOverride {
    /** Operations to preselect. More than one shows the full operator bar. */
    operations?: Operations[];
    /** Digit count for [first operand, second operand]. */
    digits?: [number, number];
    /**
     * A literal value selection per operand, for a page narrower than a whole
     * digit bucket - "addition within 5", or a single times table. Applied
     * after `digits`, which sets the bucket this selects from; the first
     * override that narrows a selection rather than widening it.
     */
    ranges?: number[][];
    regrouping?: RegroupingOptions;
    /** Leaves a cell of each fact blank: 7 + ___ = 12. Math Facts only. */
    missingNumber?: MissingNumberOptions;
    problemsPerPage?: number;
    remainders?: boolean;
    /** Prints a Time and Score box, for a page that is a drill against the clock. */
    timed?: boolean;
    /** Opens the page on a half sheet: two copies of the sheet to a page. */
    halfPage?: boolean;
    /** Which stories a word-problem page draws. Varied is ADR 0073 section 2. */
    storyTypes?: StoryTypeOptions;
    /** Opens the page as a fact fluency level test, at this level. See ADR 0071 §5. */
    factLevel?: number;
    /** How far the multiplication and division tables run. See ADR 0071 §2. */
    factLevelCeiling?: 10 | 12;
    /** Opens a level page as the home practice for it rather than the test. See ADR 0071 §8. */
    factLevelSheet?: 'test' | 'practice';
    /** What kind of problem the sheet is built from. */
    format?: ProblemFormatOptions;
    fractionDenominators?: FractionDenominatorOptions;
    maxDenominator?: number;
    moneyMaxDollars?: number;
    /** What a money sheet asks: counting coins, making change. See ADR 0018. */
    moneyQuestion?: MoneyQuestionOptions;
    /** Which coins a counting-coins page shows, by name. */
    moneyCoins?: string[];
    decimalPlaces?: number;
    decimalMaxWhole?: number;
    placeValueQuestion?: PlaceValueOptions;
    placeValueDigits?: number;
    roundingComparingQuestion?: RoundingComparingOptions;
    roundingPlace?: RoundingPlaceOptions;
    roundingComparingDigits?: number;
    roundingComparingDecimals?: number;
    /** How a rounding or comparing question is shown - plain, or a number line. See ADR 0046. */
    roundingDisplay?: RoundingDisplayOptions;
    timeQuestion?: TimeQuestionOptions;
    timeInterval?: TimeIntervalOptions;
    /** How the two times in an elapsed-time problem are shown. See ADR 0017. */
    timeDisplay?: TimeDisplayOptions;
    /** Every elapsed problem crosses noon, with a.m. and p.m. See ADR 0090 §3. */
    crossNoon?: boolean;
    /** How hard the order-of-operations expressions are. See ADR 0019. */
    orderOfOperationsLevel?: OrderOfOperationsLevelOptions;
    algebraQuestion?: AlgebraQuestionOptions;
    algebraMaxValue?: number;
    /** Lets Algebra and Order of Operations use negatives. See ADR 0036 §2. */
    allowNegatives?: boolean;
    /** What an integers sheet asks. See ADR 0030 §3. */
    integerQuestion?: IntegerQuestionOptions;
    integerMaxValue?: number;
    /** Decimals, fractions or both on the rational question types. See ADR 0122 §2. */
    rationalKind?: RationalKindOptions;
    /** What a number-properties sheet asks. See ADR 0032 §1. */
    numberPropertyQuestion?: NumberPropertyOptions;
    numberPropertyMaxValue?: number;
    /** Which unit system a conversion sheet draws from. See ADR 0032 §2. */
    conversionQuestion?: ConversionQuestionOptions;
    /** What kind of sequence a patterns sheet draws. See ADR 0032 §5. */
    patternQuestion?: PatternQuestionOptions;
    patternLength?: number;
    /** Which way a Roman numeral question runs, and how high it goes. See ADR 0079. */
    romanQuestion?: RomanQuestionOptions;
    romanMax?: number;
    /** How big the numbers in a magic square go. See ADR 0088 §4. */
    magicSquareMax?: number;
    /** What a line-plot sheet asks, its tick unit, and its mark. See ADR 0084. */
    linePlotQuestion?: LinePlotQuestionOptions;
    linePlotScale?: LinePlotScaleOptions;
    linePlotMark?: LinePlotMarkOptions;
    /** What a histogram sheet asks. See ADR 0099. */
    histogramQuestion?: HistogramQuestionOptions;
    /** What a box-plot sheet asks. See ADR 0099. */
    boxPlotQuestion?: BoxPlotQuestionOptions;
    /** What a coordinate-plane sheet asks, and on which grid. See ADR 0085. */
    coordinateQuestion?: CoordinateQuestionOptions;
    coordinateGrid?: CoordinateGridOptions;
    /** What a calendar sheet asks, how far it counts, and which month. See ADR 0093. */
    calendarQuestion?: CalendarQuestionOptions;
    calendarSpan?: CalendarSpanOptions;
    calendarMonth?: CalendarMonthOptions;
    /** Which number system a number line asks about. See ADR 0033 §2. */
    numberLineQuestion?: NumberLineQuestionOptions;
    numberLineRange?: number;
    /** What a percent sheet asks. See ADR 0034 §2. */
    percentQuestion?: PercentQuestionOptions;
    percentMaxValue?: number;
    /** What a ratio sheet asks. See ADR 0035 §2. */
    ratioQuestion?: RatioQuestionOptions;
    ratioMaxValue?: number;
    /** What a geometry sheet asks. See ADR 0049 §1. */
    geometryQuestion?: GeometryQuestionOptions;
    /** How round a geometry sheet's angles are. See ADR 0049 §6. */
    geometryAngleStep?: number;
    /** What a perimeter-and-area sheet asks. See ADR 0038 §1. */
    shapeQuestion?: ShapeQuestionOptions;
    shapeMaxSide?: number;
    /** What a volume sheet asks. See ADR 0095 §3. */
    volumeQuestion?: VolumeQuestionOptions;
    volumeMaxEdge?: number;
    /** What a circles sheet asks. See ADR 0106 §3. */
    circleQuestion?: CircleQuestionOptions;
    circleMaxRadius?: number;
    /** What a probability sheet asks. See ADR 0108 §2. */
    probabilityQuestion?: ProbabilityQuestionOptions;
    /** What a Pythagorean theorem sheet asks. See ADR 0109 §2. */
    pythagoreanQuestion?: PythagoreanQuestionOptions;
    pythagoreanMaxSide?: number;
    /** What an averages sheet asks: mean, median, mode, range. See ADR 0039 §2. */
    averageQuestion?: AverageQuestionOptions;
    averageListLength?: number;
    /** What a bar-graph sheet asks: reading, difference, total, largest. See ADR 0039 §3. */
    barGraphQuestion?: BarGraphQuestionOptions;
    barGraphMaxValue?: number;
    /** How many questions share one drawn chart. See ADR 0040 §3-4. */
    barGraphQuestionsPerChart?: number;
    /** How a fact family is asked. See ADR 0020. */
    factFamilyQuestion?: FactFamilyOptions;
    /** What a fraction sheet asks: simplify, compare, … See ADR 0021. */
    fractionQuestion?: FractionQuestionOptions;
    /** Largest denominator a fraction-model drawing may use. See ADR 0043 §3. */
    fractionModelMaxDenominator?: number;
    /** Presentation scaffolds: carry boxes, alignment grid, large print. See ADR 0014. */
    carryBoxes?: boolean;
    alignmentGrid?: boolean;
    largePrint?: boolean;
    /** The sections of a spiral-review sheet. See ADR 0013. */
    spiralSections?: { format: ProblemFormatOptions; count: number }[];
    /** Opens the page in three-tier differentiation-set mode. See ADR 0010. */
    differentiationSet?: boolean;
    /**
     * The printed worksheet language (ADR 0051 §7/§8) - worksheet state, set
     * alongside `locale` on a localised route but layered here exactly like
     * every other override rather than through `Locale`, which never touches
     * `ISettingsProps`. Stays user-changeable after load, unlike `locale`.
     */
    language?: PrintedLanguage;
}

/**
 * The selection a route's `digits` gives an operand: the whole digit bucket.
 * On a times-table sheet the range on offer is wider (0-12, ADR 0064), but the
 * selection stays 0-9 - "multiplying by one-digit numbers" means 0 to 9, and
 * a page that wants the full grid says so with `ranges`.
 */
const bucketsForDigits: Record<number, number[]> = {
    1: SingleDigitNumbers.Range,
    2: DoubleDigitNumbers.Range,
    3: TripleDigitNumbers.Range,
};

/**
 * Builds the initial generator settings for a single-operation route
 * (e.g. a multiplication landing page), starting from DefaultSettings and
 * pinning the operation. Shared by App.js routes and the Landing pages.
 */
export function worksheetgeneratorSettings(
    operation: Operations,
    createWordProblems: boolean = false
): ISettingsProps {
    const settings = cloneDeep(DefaultSettings);
    settings.AllOperations = false;
    settings.Page = operation;
    settings.Operators = [OperationOptions.filter((op) => op.value === operation)[0]];
    settings.Format = createWordProblems
        ? ProblemFormatOptions.WordProblem
        : ProblemFormatOptions.MathFacts;
    // Offers 0-12 on a multiplication or division page (ADR 0064).
    return fitOneDigitRanges(settings);
}

/**
 * Layers a route's manifest overrides onto a base set of settings.
 *
 * Returns a clone; the input is left alone. An override that names no
 * operations leaves the base operator selection as it found it, so a route can
 * pin only its number ranges.
 */
export function applyRouteSettings(
    base: ISettingsProps,
    override?: RouteSettingsOverride
): ISettingsProps {
    if (!override) {
        return base;
    }
    const settings = cloneDeep(base);

    if (override.operations && override.operations.length > 0) {
        settings.Operators = OperationOptions.filter((op) =>
            override.operations!.includes(op.value)
        );
        settings.Page = override.operations[0];
        // A page covering more than one operation shows the whole operator bar
        // with its own operations already ticked, rather than a single pinned
        // button that would contradict the worksheet being generated.
        settings.AllOperations = override.operations.length > 1;
    }

    if (override.digits) {
        override.digits.forEach((digits, index) => {
            const range = settings.NumberRanges[index];
            const buckets = bucketsForDigits[digits];
            if (!range || !buckets) {
                return;
            }
            range.NumberOfDigits = digits;
            range.NumberRange = [...bucketFor(digits, operationsOf(settings)).Range];
            // Landing pages select the whole range. The single-bucket default
            // the UI applies when you click "2" is a convenience for someone
            // narrowing by hand, not what a page about two-digit work means.
            range.NumberRangeSelection = [...buckets];
        });
    }

    if (override.ranges) {
        // After `digits`, so the bucket is set first and this picks from it.
        // A missing or empty entry leaves that operand's selection alone,
        // matching how `digits` skips an index it has no bucket for.
        override.ranges.forEach((selection, index) => {
            const range = settings.NumberRanges[index];
            if (!range || !selection || selection.length === 0) {
                return;
            }
            range.NumberRangeSelection = [...selection];
        });
    }

    if (override.regrouping) {
        settings.Regrouping = override.regrouping;
    }
    if (override.missingNumber) {
        settings.MissingNumber = override.missingNumber;
    }
    if (override.problemsPerPage) {
        settings.NumberOfProblemsPerPage = override.problemsPerPage;
    }
    if (override.remainders !== undefined) {
        settings.Remainders = override.remainders;
    }
    if (override.timed !== undefined) {
        settings.Timed = override.timed;
    }
    if (override.halfPage !== undefined) {
        settings.HalfPage = override.halfPage;
    }
    if (override.storyTypes) {
        settings.StoryTypes = override.storyTypes;
    }
    if (override.factLevel !== undefined) {
        settings.FactLevel = override.factLevel;
    }
    if (override.factLevelCeiling !== undefined) {
        settings.FactLevelCeiling = override.factLevelCeiling;
    }
    if (override.factLevelSheet) {
        settings.FactLevelSheet = override.factLevelSheet;
    }
    if (override.format) {
        settings.Format = override.format;
    }
    if (override.fractionDenominators) {
        settings.FractionDenominators = override.fractionDenominators;
    }
    if (override.maxDenominator) {
        settings.MaxDenominator = override.maxDenominator;
    }
    if (override.moneyMaxDollars) {
        settings.MoneyMaxDollars = override.moneyMaxDollars;
    }
    if (override.moneyQuestion) {
        settings.MoneyQuestion = override.moneyQuestion;
    }
    if (override.moneyCoins) {
        settings.MoneyCoins = [...override.moneyCoins];
    }
    if (override.decimalPlaces) {
        settings.DecimalPlaces = override.decimalPlaces;
    }
    if (override.decimalMaxWhole) {
        settings.DecimalMaxWhole = override.decimalMaxWhole;
    }
    if (override.placeValueQuestion) {
        settings.PlaceValueQuestion = override.placeValueQuestion;
    }
    if (override.placeValueDigits) {
        settings.PlaceValueDigits = override.placeValueDigits;
    }
    if (override.roundingComparingQuestion) {
        settings.RoundingComparingQuestion = override.roundingComparingQuestion;
    }
    if (override.roundingPlace) {
        settings.RoundingPlace = override.roundingPlace;
    }
    if (override.roundingComparingDigits) {
        settings.RoundingComparingDigits = override.roundingComparingDigits;
    }
    if (override.roundingComparingDecimals !== undefined) {
        settings.RoundingComparingDecimals = override.roundingComparingDecimals;
    }
    if (override.roundingDisplay) {
        settings.RoundingDisplay = override.roundingDisplay;
    }
    if (override.timeQuestion) {
        settings.TimeQuestion = override.timeQuestion;
    }
    if (override.timeInterval) {
        settings.TimeInterval = override.timeInterval;
    }
    if (override.timeDisplay) {
        settings.TimeDisplay = override.timeDisplay;
    }
    if (override.crossNoon !== undefined) {
        settings.CrossNoon = override.crossNoon;
    }
    if (override.shapeQuestion) {
        settings.ShapeQuestion = override.shapeQuestion;
    }
    if (override.shapeMaxSide) {
        settings.ShapeMaxSide = override.shapeMaxSide;
    }
    if (override.volumeQuestion) {
        settings.VolumeQuestion = override.volumeQuestion;
    }
    if (override.volumeMaxEdge) {
        settings.VolumeMaxEdge = override.volumeMaxEdge;
    }
    if (override.circleQuestion) {
        settings.CircleQuestion = override.circleQuestion;
    }
    if (override.circleMaxRadius) {
        settings.CircleMaxRadius = override.circleMaxRadius;
    }
    if (override.probabilityQuestion) {
        settings.ProbabilityQuestion = override.probabilityQuestion;
    }
    if (override.pythagoreanQuestion) {
        settings.PythagoreanQuestion = override.pythagoreanQuestion;
    }
    if (override.pythagoreanMaxSide) {
        settings.PythagoreanMaxSide = override.pythagoreanMaxSide;
    }
    if (override.averageQuestion) {
        settings.AverageQuestion = override.averageQuestion;
    }
    if (override.averageListLength) {
        settings.AverageListLength = override.averageListLength;
    }
    if (override.barGraphQuestion) {
        settings.BarGraphQuestion = override.barGraphQuestion;
    }
    if (override.barGraphMaxValue) {
        settings.BarGraphMaxValue = override.barGraphMaxValue;
    }
    if (override.barGraphQuestionsPerChart) {
        settings.BarGraphQuestionsPerChart = override.barGraphQuestionsPerChart;
    }
    if (override.geometryQuestion) {
        settings.GeometryQuestion = override.geometryQuestion;
    }
    if (override.geometryAngleStep) {
        settings.GeometryAngleStep = override.geometryAngleStep;
    }
    if (override.ratioQuestion) {
        settings.RatioQuestion = override.ratioQuestion;
    }
    if (override.ratioMaxValue) {
        settings.RatioMaxValue = override.ratioMaxValue;
    }
    if (override.percentQuestion) {
        settings.PercentQuestion = override.percentQuestion;
    }
    if (override.percentMaxValue) {
        settings.PercentMaxValue = override.percentMaxValue;
    }
    if (override.numberLineQuestion) {
        settings.NumberLineQuestion = override.numberLineQuestion;
    }
    if (override.numberLineRange) {
        settings.NumberLineRange = override.numberLineRange;
    }
    if (override.patternQuestion) {
        settings.PatternQuestion = override.patternQuestion;
    }
    if (override.patternLength) {
        settings.PatternLength = override.patternLength;
    }
    if (override.romanQuestion) {
        settings.RomanQuestion = override.romanQuestion;
    }
    if (override.romanMax) {
        settings.RomanMax = override.romanMax;
    }
    if (override.magicSquareMax) {
        settings.MagicSquareMax = override.magicSquareMax;
    }
    if (override.linePlotQuestion) {
        settings.LinePlotQuestion = override.linePlotQuestion;
    }
    if (override.linePlotScale) {
        settings.LinePlotScale = override.linePlotScale;
    }
    if (override.linePlotMark) {
        settings.LinePlotMark = override.linePlotMark;
    }
    if (override.histogramQuestion) {
        settings.HistogramQuestion = override.histogramQuestion;
    }
    if (override.boxPlotQuestion) {
        settings.BoxPlotQuestion = override.boxPlotQuestion;
    }
    if (override.coordinateQuestion) {
        settings.CoordinateQuestion = override.coordinateQuestion;
    }
    if (override.coordinateGrid) {
        settings.CoordinateGrid = override.coordinateGrid;
    }
    if (override.calendarQuestion) {
        settings.CalendarQuestion = override.calendarQuestion;
    }
    if (override.calendarSpan) {
        settings.CalendarSpan = override.calendarSpan;
    }
    if (override.calendarMonth) {
        settings.CalendarMonth = override.calendarMonth;
    }
    if (override.conversionQuestion) {
        settings.ConversionQuestion = override.conversionQuestion;
    }
    if (override.numberPropertyQuestion) {
        settings.NumberPropertyQuestion = override.numberPropertyQuestion;
    }
    if (override.numberPropertyMaxValue) {
        settings.NumberPropertyMaxValue = override.numberPropertyMaxValue;
    }
    if (override.integerQuestion) {
        settings.IntegerQuestion = override.integerQuestion;
    }
    if (override.integerMaxValue) {
        settings.IntegerMaxValue = override.integerMaxValue;
    }
    if (override.rationalKind) {
        settings.RationalKind = override.rationalKind;
    }
    if (override.allowNegatives !== undefined) {
        settings.AllowNegatives = override.allowNegatives;
    }
    if (override.algebraQuestion) {
        settings.AlgebraQuestion = override.algebraQuestion;
    }
    if (override.algebraMaxValue) {
        settings.AlgebraMaxValue = override.algebraMaxValue;
    }
    if (override.orderOfOperationsLevel) {
        settings.OrderOfOperationsLevel = override.orderOfOperationsLevel;
    }
    if (override.factFamilyQuestion) {
        settings.FactFamilyQuestion = override.factFamilyQuestion;
    }
    if (override.fractionQuestion) {
        settings.FractionQuestion = override.fractionQuestion;
    }
    if (override.fractionModelMaxDenominator) {
        settings.FractionModelMaxDenominator = override.fractionModelMaxDenominator;
    }
    if (override.spiralSections) {
        settings.SpiralSections = override.spiralSections.map((s) => ({ ...s }));
    }
    if (override.carryBoxes !== undefined || override.alignmentGrid !== undefined || override.largePrint !== undefined) {
        settings.Presentation = {
            carryBoxes: override.carryBoxes ?? settings.Presentation.carryBoxes,
            alignmentGrid: override.alignmentGrid ?? settings.Presentation.alignmentGrid,
            largePrint: override.largePrint ?? settings.Presentation.largePrint,
        };
    }
    if (override.differentiationSet !== undefined) {
        settings.DifferentiationSet = override.differentiationSet;
    }
    if (override.language) {
        settings.Language = override.language;
    }

    // Last, so a route that changed the operations offers the range that goes
    // with them (ADR 0064 §3), and a level its ladder has (ADR 0071 §5).
    return fitFactLevel(fitOneDigitRanges(settings));
}

/** The settings a manifest route opens with, overrides included. */
export function settingsForRoute(route: {
    operation?: Operations;
    settings?: RouteSettingsOverride;
}): ISettingsProps {
    const base = route.operation
        ? worksheetgeneratorSettings(route.operation)
        : cloneDeep(DefaultSettings);
    return applyRouteSettings(base, route.settings);
}

/**
 * The settings a landing page opens with when it is reached through a
 * worksheet link (`isWorksheetLink`): the linked sheet, not the route's own.
 *
 * The link carries every setting but two, which have no URL key because they
 * describe the page rather than the sheet: `Page`, the operation the page is
 * titled after, and `AllOperations`, whether its operator bar offers every
 * operation or only that one. Both come from the route. If the link names an
 * operation the page does not pin - only a hand-edited link can - the whole
 * bar is shown, so the bar never contradicts the sheet beside it.
 */
export function settingsForSharedLink(
    route: { operation?: Operations; settings?: RouteSettingsOverride },
    queryString: string
): ISettingsProps {
    const routeSettings = settingsForRoute(route);
    const linked: ISettingsProps = ProcessUrl(queryString);
    const offPage = (linked.Operators ?? []).some(
        (operator) => operator.value !== routeSettings.Page
    );
    return {
        ...linked,
        Page: routeSettings.Page,
        AllOperations: routeSettings.AllOperations || offPage,
    };
}
